/**
 * Teacher list for a study group with an inline add form.
 */

import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { TrashIcon } from '@components/icons';

interface GroupTeachersProps {
  groupId: string;
}

export const GroupTeachers = observer(function GroupTeachers({ groupId }: GroupTeachersProps) {
  const group = bibleState.getStudyGroup(groupId);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');

  if (!group) return null;

  const reset = () => {
    setIsAdding(false);
    setNewName('');
  };

  const submit = () => {
    if (!newName.trim()) return;
    bibleState.addTeacherToGroup(groupId, newName.trim());
    setNewName('');
  };

  const teachers = group.teachers || [];

  return (
    <div className="mt-4 border-t border-stone-200 pt-3 dark:border-stone-700">
      <h4 className="mb-1 text-sm font-medium text-stone-700 dark:text-stone-300">Teachers</h4>
      <p className="mb-2 text-xs text-stone-500 dark:text-stone-400">
        One teacher is implied — the study always prints a teacher sheet. Add teachers to
        print a sheet for each by name.
      </p>
      {teachers.length > 0 && (
        <div className="flex flex-col gap-2">
          {teachers.map((teacher) => (
            <div key={teacher.id} className="flex items-center justify-between py-1 pl-6">
              <div className="text-sm text-stone-700 dark:text-stone-300">{teacher.name}</div>
              <button
                onClick={() => bibleState.removeTeacherFromGroup(groupId, teacher.id)}
                className="p-1 text-stone-400 transition-colors hover:text-red-500"
                title="Remove teacher"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {isAdding ? (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Teacher name..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) submit();
              if (e.key === 'Escape') reset();
            }}
            className="flex-1 rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200"
          />
          <button
            onClick={submit}
            disabled={!newName.trim()}
            className="rounded-lg bg-cyan-500 px-2.5 py-1.5 text-xs text-white transition-colors hover:bg-cyan-600 disabled:opacity-50"
          >
            Add
          </button>
          <button
            onClick={reset}
            className="text-xs text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200"
          >
            Done
          </button>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          data-guide="add-teacher"
          className="mt-2 text-sm text-cyan-600 hover:underline dark:text-cyan-400"
        >
          + Add teacher
        </button>
      )}
    </div>
  );
});
